import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

/**
 * Custom hook for checking Smartlead integration status
 * Used to conditionally render/enable Export to Smartlead functionality
 * 
 * @returns {Object} - Integration status and operations
 */
const useSmartleadIntegration = () => {
  const [status, setStatus] = useState('disconnected');
  const [isLoading, setIsLoading] = useState(true);
  const [connectedAt, setConnectedAt] = useState(null);
  const [lastVerifiedAt, setLastVerifiedAt] = useState(null);

  /**
   * Fetch Smartlead integration status from backend
   */
  const fetchStatus = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await api.get('/api/integrations/smartlead/status');
      const { status: integrationStatus, connected_at, last_verified_at } = response.data;
      
      setStatus(integrationStatus);
      setConnectedAt(connected_at);
      setLastVerifiedAt(last_verified_at);
    } catch (error) {
      console.error('Failed to fetch Smartlead integration status:', error);
      // If API fails, assume disconnected
      setStatus('disconnected');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Fetch status on mount
  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // Listen for integration changes (e.g., when user connects/disconnects in another tab)
  useEffect(() => {
    const handleIntegrationChange = () => {
      fetchStatus();
    };

    window.addEventListener('smartleadIntegrationChanged', handleIntegrationChange);
    return () => {
      window.removeEventListener('smartleadIntegrationChanged', handleIntegrationChange);
    };
  }, [fetchStatus]);

  return {
    status,
    isConnected: status === 'active',
    isLoading,
    connectedAt,
    lastVerifiedAt,
    refetch: fetchStatus,
  };
};

export default useSmartleadIntegration;

